import React, { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { UserPlus, Mail, Lock, Sparkles, ShieldCheck, ShieldAlert, Eye, EyeOff, Info } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import './Login.css';
import './Register.css';
import logo from '../assets/logo.png';
import githubLogo from '../assets/githubLogo.png';
import googleLogo from '../assets/googleLogo.png';
import { isSupabaseConfigured } from '../services/supabaseClient';

const isTrustedLocalhost = (hostname) => {
  const normalized = (hostname || '').toLowerCase();
  if (['localhost', '127.0.0.1', '::1', '0.0.0.0'].includes(normalized)) {
    return true;
  }
  return normalized.endsWith('.localhost') || normalized.endsWith('.local') || normalized.endsWith('.test');
};

const initialFormState = {
  fullName: '',
  email: '',
  password: ''
};

const Register = () => {
  const [formData, setFormData] = useState(initialFormState);
  const [showPassword, setShowPassword] = useState(false);
  const [showPolicyPopup, setShowPolicyPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const { register, getOAuthUrl } = useAuth();
  const supabaseReady = isSupabaseConfigured;
  const isSecureContext = useMemo(() => {
    if (typeof window === 'undefined') {
      return true;
    }
    return window.location.protocol === 'https:' || isTrustedLocalhost(window.location.hostname);
  }, []);

  const handleChange = (field) => (event) => {
    setFormData((previous) => ({
      ...previous,
      [field]: event.target.value
    }));
  };

  const handleEmailChange = (event) => {
    const sanitizedValue = event.target.value.replace(/\s+/g, '');
    setFormData((previous) => ({
      ...previous,
      email: sanitizedValue
    }));
  };

  const validateForm = () => {
    const email = formData.email.trim();
    if (!email || !formData.password) {
      return 'Email and password fields are required.';
    }

    const password = formData.password;
    if (password.length < 12) {
      return 'Password must be at least 12 characters long.';
    }
    if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/\d/.test(password) || !/[^A-Za-z0-9]/.test(password)) {
      return 'Choose a password with uppercase, lowercase, number, and symbol characters.';
    }

    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');



    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      if (!isSecureContext) {
        throw new Error('Switch to an HTTPS connection before creating an account.');
      }

      // Use backend registration via context
      const result = await register(formData);

      if (!result.success) {
        throw new Error(result.error || 'Unable to create account. Please try again later.');
      }

      setSuccess('Registration successful!');
      setFormData(initialFormState);

      setTimeout(() => {
        navigate('/login');
      }, 3200);
    } catch (registrationError) {
      console.error('Registration error:', registrationError);
      setError(registrationError?.message || 'Unable to complete registration right now. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthRegister = async (provider) => {
    if (!supabaseReady) {
      setError('OAuth signup requires Supabase configuration');
      return;
    }

    setError('');
    setLoading(true);

    try {
      if (!isSecureContext) {
        throw new Error('Switch to an HTTPS connection before creating an account.');
      }

      const result = await getOAuthUrl(provider);
      if (!result?.success) {
        throw new Error(result?.error || `Failed to start ${provider} sign-up`);
      }

      window.location.href = result.url;
    } catch (oauthError) {
      console.error(`${provider} OAuth error:`, oauthError);
      setError(oauthError?.message || `Unable to sign up with ${provider}`);
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="login-page register-page">
        <div className="login-page__frame">
          <header className="login-brand">
            <div className="login-brand__inner">
              <span className="login-brand__tab" aria-hidden="true" />
              <div className="login-brand__logo-wrap">
                <img src={logo} alt="LinkLoad Logo" className="login-brand__logo" />
              </div>
              <div className="login-brand__text">
                <h1 className="login-brand__title">Breaches Hate Company.</h1>
                <p className="login-brand__subtitle">
                  We Don't Just Connect the Dots. We Obliterate the Threat.
                </p>
              </div>
              <span className="login-brand__chip" aria-hidden="true" />
            </div>
          </header>

          <section className="login-card register-card">
            <div className="login-card__surface">
              <div className="login-card__header">
                <div className="login-card__badge">
                  <UserPlus size={30} color="#000" />
                </div>
                <h1 className="login-card__title">Create your account</h1>
              </div>

              {!isSecureContext && (
                <div className="login-alert" role="alert" style={{ background: 'rgba(241, 86, 63, 0.12)' }}>
                  <ShieldAlert size={18} />
                  <span>A secure HTTPS connection is required to protect registration details. Switch to an encrypted link before continuing.</span>
                </div>
              )}

              {error && (
                <div className="login-alert" role="alert" style={{ background: 'rgba(241, 86, 63, 0.12)' }}>
                  <ShieldAlert size={18} />
                  <span>{error}</span>
                </div>
              )}

              {!supabaseReady && (
                <div className="login-alert" role="alert" style={{ background: 'rgba(241, 86, 63, 0.12)' }}>
                  <ShieldAlert size={18} />
                  <span>
                    Supabase authentication is not fully configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to enable OAuth sign-up.
                  </span>
                </div>
              )}

              {success && (
                <div className="login-alert" role="status" style={{ background: 'rgba(113, 208, 140, 0.2)' }}>
                  <Sparkles size={18} />
                  <span>{success}</span>
                </div>
              )}

              <form className="login-form" onSubmit={handleSubmit}>
                <div className="login-input-group">
                  <label className="login-input-label" htmlFor="register-fullname">
                    <UserPlus size={16} />
                    Full name (optional)
                  </label>
                  <input
                    id="register-fullname"
                    type="text"
                    className="login-input"
                    placeholder="Captain Link"
                    value={formData.fullName}
                    onChange={handleChange('fullName')}
                    autoComplete="name"
                  />
                </div>

                <div className="login-input-group">
                  <label className="login-input-label" htmlFor="register-email">
                    <Mail size={16} />
                    Work email
                  </label>
                  <input
                    id="register-email"
                    type="email"
                    className="login-input"
                    placeholder="captain@linkload.in"
                    value={formData.email}
                    onChange={handleEmailChange}
                    autoComplete="email"
                    required
                  />
                </div>

                <div className="login-input-group">
                  <label className="login-input-label" htmlFor="register-password" style={{ display: 'flex', alignItems: 'center' }}>
                    <Lock size={16} />
                    Password
                    <button 
                      type="button" 
                      onClick={() => setShowPolicyPopup(true)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', marginLeft: 'auto' }}
                      title="View Password Requirements"
                    >
                      <Info size={16} color="var(--color-black)" />
                    </button>
                  </label>
                  <div className="login-input-wrapper">
                    <input
                      id="register-password"
                      type={showPassword ? 'text' : 'password'}
                      className="login-input"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange('password')}
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      className="login-input-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="login-submit"
                  disabled={loading || !isSecureContext}
                  aria-disabled={loading || !isSecureContext}
                  style={!isSecureContext ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                >
                  {loading ? 'Creating account…' : !isSecureContext ? 'Enable HTTPS' : 'Create account'}
                </button>
              </form>

              {supabaseReady && (
                <>
                  <div className="login-divider">
                    <span className="login-divider__line" />
                    <span className="login-divider__text">Or continue with</span>
                    <span className="login-divider__line" />
                  </div>

                  <div className="login-oauth">
                    <button
                      type="button"
                      className="login-oauth__button login-oauth__button--github"
                      onClick={() => handleOAuthRegister('github')}
                      disabled={loading || !isSecureContext}
                    >
                      <span className="login-oauth__glyph" aria-hidden="true">
                        <img src={githubLogo} alt="" className="login-oauth__logo" />
                      </span>
                      <span>GitHub</span>
                    </button>
                    <button
                      type="button"
                      className="login-oauth__button login-oauth__button--google"
                      onClick={() => handleOAuthRegister('google')}
                      disabled={loading || !isSecureContext}
                    >
                      <span className="login-oauth__glyph" aria-hidden="true">
                        <img src={googleLogo} alt="" className="login-oauth__logo" />
                      </span>
                      <span>Google</span>
                    </button>
                  </div>
                </>
              )}

              {showPolicyPopup && (
                <div className="retro-modal-overlay">
                  <div className="retro-modal-content">
                    <div className="retro-modal-header">
                      <h3>PASSWORD DIRECTIVE</h3>
                    </div>
                    <div className="retro-modal-body">
                      Passwords must be at least 12 characters long and include uppercase, lowercase, numeric, and symbol characters.
                    </div>
                    <div className="retro-modal-footer">
                      <button type="button" onClick={() => setShowPolicyPopup(false)} className="retro-modal-ok">ACKNOWLEDGE</button>
                    </div>
                  </div>
                </div>
              )}

              <div className="login-meta">
                Already have an account?
                <Link className="login-meta__link" to="/login">
                  Sign in instead
                </Link>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
};

export default Register;